import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';
import { ColumnNote } from '../../../../models/locations-models/column-note';
import { Location } from '../../../../models/locations-models/location';
import { HttpErrorResponse } from '@angular/common/http';
import { GbpUpdateType } from '../popups/get-gbp-updates-popup/get-gbp-updates-popup.component';
import { ApiResponse } from '../../../../models/api-response';
import { LSCField } from '../../../../models/LSCFields';
import { GbpLocationGroup } from '../../../../models/locations-models/gbp-location-group';
import { OngoingActionsEnum } from '../../enums/ongoing-actions';
import { LocationAddressTranslation } from '../../../../models/locations-models/location-address-translation';
import { LocationDetail } from '../../enums/location-detail';
import { DatasourceSyncDetails } from '../../../../models/datasource-sync-details';
import { CCSyncDetails } from '../../../../models/locations-models/cc-sync-details';
import { CoordinateHistory } from '../../../../models/coordinate-history';
import { BulkValidationDetails, BulkValidationPayload } from '../../../../models/bulk-validation-details';

export const LocationsRefreshActions = createActionGroup({
  source: 'LOCATION LIST REFRESH',
  events: {
    getEntireLocationList: props<{ pageSize?: number, requestedPage?: number }>(), // request
    getEntireLocationListSuccess: props<{ locations: Location[], lastSuccessfulPageSize: number }>(), // store update
    patchLocation: props<{ location: Location }>(), // store update
    patchLocations: props<{ locations: Location[] }>(), // store update
    ignoreUpdateError: props<{ location: Location }>(), // store update
    removedLocations: props<{ ids: string[] }>() // store update
  }
});

export const GbpLocationGroupsActions = createActionGroup({
  source: 'LOCATION LIST - GBP L. GRPs REFRESH',
  events: {
    updateGbpLocationGroups: emptyProps(), // request
    updateGbpLocationGroupsSuccess: props<{ gbpLocationGroups: GbpLocationGroup[] }>(), // store update
    updateGbpLocationGroupsError: emptyProps()
  }
});

export const EditLocationActions = createActionGroup({
  source: 'LOCATION LIST - EDIT LOCATION',
  events: {
    editLocationData: props<{ location: Location, singleField?: LSCField, updateCCList?: boolean }>(), // request
    editLocationDataSuccess: props<{ location: Location, singleField?: LSCField }>(), // store update
    editLocationDataError: props<{ location: Location, singleField?: LSCField }>()
  }
});

export const CCCompletionActions = createActionGroup({
  source: 'LOCATION LIST - CC COMPLETION TIMESTAMP UPDATE',
  events: {
    updateCcCompletionTimestamp: props<{ location: Location, completed: boolean }>(), // request
    updateCcCompletionTimestampSuccess: props<{ location: Location }>(), // store update
    updateCcCompletionTimestampError: props<{ location: Location }>()
  }
});

export const OngoingActions = createActionGroup({
  source: 'LOCATION LIST - ONGOING ACTIONS REFRESH ',
  events: {
    addOngoingAction: props<{ ongoingAction: OngoingActionsEnum }>(),
    removeOngoingAction: props<{ ongoingAction: OngoingActionsEnum }>(),
  }
});

export const RowActions = createActionGroup({
  source: 'LOCATION LIST - ROW OPERATION',
  events: {
    userFocusRow: props<{ id: number }>(), // checks if it's safe to change focus
    updateFocusedRow: props<{ id: number }>(),
    updateHoveredRowId: props<{ hoveredRowId: number }>(),
    updateSelectedRowsIds: props<{ selectedRowsIds: number[] }>(),
  }
});

export const AddressTranslationActions = createActionGroup({
  source: 'LOCATION LIST - ADDRESS TRANSLATION',
  events: {
    getAddressTranslation: props<{ id: number }>(), // request
    getAddressTranslationSuccess: props<{ addressTranslation: LocationAddressTranslation }>(), // store update
    getAddressTranslationFail: props<{ id: number }>(),
    removeAddressTranslation: props<{ id: number }>(),
  }
});

export const LoadingSpinnerActions = createActionGroup({
  source: 'LOCATION LIST - LOADING SPINNER',
  events: {
    showLoadingSpinner: emptyProps(),
    stopLoadingSpinner: emptyProps(),
  }
});

export const LocationsLifecycleActions = createActionGroup({
  source: 'LOCATION LIST - COMPONENT LIFECYCLE',
  events: {
    locationsComponentInitiated: props<{ pageSize?: number, requestedPage?: number }>(),
    locationsComponentDestroyed: emptyProps(),
    clearLocationsState: emptyProps()
  }
});

export const CountryListActions = createActionGroup({
  source: 'LOCATION LIST - COUNTRY LIST',
  events: {
    updateCountryList: emptyProps(), // request
    updateCountryListSuccess: props<{ countryList: string[] }>(), // store update
  }
});

export const ActiveDataConnectionsActions = createActionGroup({
  source: 'LOCATION LIST - DATA CONNECTIONS',
  events: {
    loadActiveDataConnections: emptyProps(), // request
    loadActiveDataConnectionsSuccess: props<{ activeDataConnections: string[] }>(), // store update
  }
});

export const ColumnNotesActions = createActionGroup({
  source: 'LOCATION LIST - COLUMN NOTES',
  events: {
    loadColumnNotes: emptyProps(), // request
    loadColumnNotesSuccess: props<{ columnNotes: ColumnNote[] }>(), // store update
    updateColumnNote: props<{ columnNote: ColumnNote }>(), // request
    updateColumnNoteSuccess: props<{ columnNote: ColumnNote }>(), // store update
    updateColumnNoteError: props<{ columnNote: ColumnNote }>(),
    deleteColumnNote: props<{ columnNote: ColumnNote }>(),
    deleteColumnNoteSuccess: props<{ columnNoteName: string }>(),
    deleteColumnNoteError: props<{ columnNote: ColumnNote }>(),
  }
});

export const BlacklistingActions = createActionGroup({
  source: 'LOCATION LIST - BLACKLISTING',
  events: {
    blacklistFields: props<{ locations: Location[], displayedColumns: string[] }>(), // bulk BL request
    sendBlacklistFieldsRequest: props<{
      accountId: number, // per location BL request (diff component)
      locationIds: number[],
      fieldsToBeBlacklisted: string[],
      fieldsToBeWhitelisted: string[] }>(),
    blacklistFieldsSuccess: props<{ locations: Location[] }>(),
    blacklistFieldsError: props<{ err: HttpErrorResponse }>(),
  }
});

export const RefreshGbpConnectionsActions = createActionGroup({
  source: 'LOCATION LIST - REFRESH GBP CONNECTIONS',
  events: {
    refreshGbpConnections: props<{ locations: Location[] }>(), // show popup
    sendRefreshGbpConnectionsRequest: props<{  // request
      accountId: number,
      locationIds: number[],
      dataToSync: GbpUpdateType[] }>(),
    refreshGbpConnectionsSuccess: props<{ locations: Location[] }>(),
    refreshGbpConnectionsError: props<{ err: HttpErrorResponse }>(),
  }
});

export const EditGbpConnectionsActions = createActionGroup({
  source: 'LOCATION LIST - EDIT GBP CONNECTIONS',
  events: {
    editGbpConnections: props<{ location: Location, command: string }>(), // show popup
    sendEditGbpConnectionsRequest: props<{ accountId: number, locationId: number, command: string }>(), //request
    editGbpConnectionsSuccess: props<{ res: ApiResponse<Location>, command: string }>(),
    editGbpConnectionsError: props<{ err: HttpErrorResponse, command: string }>(),
  }
});

export const SyncActions = createActionGroup({
  source: 'LOCATION LIST - SYNC DETAILS',
  events: {
    getDatasourceSyncDetails: props<{ // request
      restartDatasourceSync?: boolean }>(), // schedule datasource sync afterward
    getDatasourceSyncDetailsSuccess: props<{ datasourceSyncDetails: DatasourceSyncDetails }>(), // store update,
    getDatasourceSyncDetailsError: emptyProps(),
    restartDatasourceSync: props<{ triggeredByUser?: boolean }>(), // request (if triggered by user, get datasourceSyncDetails first)
    restartDatasourceSyncSuccess: props<{ datasourceSyncDetails: DatasourceSyncDetails }>(), // store update
    restartDatasourceSyncError: emptyProps(),
    getCCSyncDetails: emptyProps(),
    getCCSyncDetailsSuccess: props<{ ccSyncDetails: CCSyncDetails }>(), // store update,
    getCCSyncDetailsError: emptyProps(),
    restartCCSync: emptyProps(),
    restartCCSyncSuccess: props<{ ccSyncDetails: CCSyncDetails }>(), // store update,
    restartCCSyncError: emptyProps(),
  }
});

export const ValidationHistoryActions = createActionGroup({
  source: 'LOCATION LIST - VALIDATION HISTORY',
  events: {
    getValidationHistory: props<{ locationId: number, lastValidationOnly?: boolean }>(),
    getValidationHistorySuccess: props<{ validationHistory: CoordinateHistory[] }>(),
    getValidationHistoryFail: emptyProps(),
    resetValidationHistory: emptyProps(),
  },
});

export const BulkValidationActions = createActionGroup({
  source: 'LOCATION LIST - BULK VALIDATION',
  events: {
    bulkValidate: props<{ payload: BulkValidationPayload }>(),
    bulkValidateSuccess: emptyProps(),
    bulkValidateFail: emptyProps(),
    getBulkValidationDetails: emptyProps(),
    getBulkValidationDetailsSuccess: props<{ details: BulkValidationDetails }>(),
    getBulkValidationDetailsFail: emptyProps(),
  },
});

export const markerSvgClicked = createAction('[LOCATION/CC LIST - ROW MENU] marker clicked',
    props<{ location: Location }>()); // center map view on marker
export const toggleLocationDetail = createAction('[LOCATION LIST] toggle detail view',
    props<{ id: number, detail: LocationDetail }>());
